import type { PayloadAction } from '@reduxjs/toolkit';
import type { EDSV2Response } from '@/types/eds/api/responses/EDSV2Response';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { PaginationResponse } from '@/types/pagination';
import { GetPaymentDetailsPayload } from '../types/GetPayeeDetailsPayload';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { AdjustmentEntities } from '@/domains/adjustments/enums/AdjustmentEntities';
import { getBenefitSectionDetails } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import {
  createPaymentTransaction,
  deletePayment,
  deletePaymentPayee,
  deletePaymentSplit,
  duplicatePaymentTransaction,
  getPaymentDetails,
  getPaymentDetailsError,
  getPaymentDetailsSuccess,
  getPaymentTransactionDetailsError,
  getPaymentTransactionDetailsSuccess,
  getPaymentTransactionsDetails,
  getPayments,
  getPaymentsSuccess,
  incrementPaymentTransactionsPage,
  startLoadingPaymentTransactionsDetails,
} from '@/domains/payments/features/PaymentsSection/store/payments.slice';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import { addPaymentSuccess } from '../../paymentModals/modals/AddPayment/store/addPayment.slice';
import { PaymentTransactionStatuses } from '../enums/PaymentTransactionStatuses';
import {
  selectHideCompleteTransactions,
  selectPaymentTransactionsDetailsPagination,
  selectPaymentTransactionsDetailsSorting,
} from '../store/payments.selectors';

function* getPaymentTransactionsDetailsSaga({ type, payload }: PayloadAction<{ paymentId: string }>) {
  try {
    yield put(startLoadingPaymentTransactionsDetails({ paymentId: payload.paymentId }));

    const TRANSACTIONS_INCLUDES = [`${EdsEntityCodes.PaymentMethod}.${EdsEntityCodes.Address}`].join(',');

    const pagination = yield select(selectPaymentTransactionsDetailsPagination(payload.paymentId));
    const sorting = yield select(selectPaymentTransactionsDetailsSorting(payload.paymentId));
    const hideCompleteStatus = yield select(selectHideCompleteTransactions(payload.paymentId));

    const filters = [
      { propertyName: EdsFilterEntities.PaymentId, values: [payload.paymentId] },
      ...(hideCompleteStatus
        ? [
            {
              propertyName: EdsFilterEntities.TransactionStatus,
              values: [
                PaymentTransactionStatuses.Pending,
                PaymentTransactionStatuses.ReadyForPayment,
                PaymentTransactionStatuses.Active,
              ],
            },
          ]
        : []),
    ];
    const requestUrl = `/filter/${EdsEntityCodes.Transaction}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination.page,
      perPage: pagination.perPage,
      sortBy: edsToEntityFormat(sorting.sortBy),
      sortDirection: sorting.sortDirection,
      includes: TRANSACTIONS_INCLUDES,
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

    EdsDebugService.logResponse(EdsEntityCodes.Transaction, {
      rawResponse: data,
      normalizedResponse,
    });

    yield put(
      getPaymentTransactionDetailsSuccess({
        paymentId: payload.paymentId,
        shouldRewrite: type !== incrementPaymentTransactionsPage.type,
        data: {
          listings: normalizedResponse,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
          sorting,
          hideCompleteTransactions: hideCompleteStatus,
        },
      }),
    );
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Transactions fetching error',
      content: 'Unable to fetch transactions. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    yield put(getPaymentTransactionDetailsError({ paymentId: payload.paymentId }));
  }
}

function* getPaymentsSaga(action: PayloadAction<{ claimId: number }>) {
  try {
    const {
      data,
    }: {
      data: {
        listings: EDSV2Response;
        pagination: PaginationResponse;
      };
    } = yield call(
      HttpService.post,
      `/filter/${EdsEntityCodes.Payment}?includes=PaymentSchedule.PaymentSplits.PaymentMethod,Adjustments.Payment.PaymentSchedule,Adjustments.Payment.Payee.Party,Payee.Party,Adjustments.AdjustmentType&page=1&perPage=1000`,
      [
        {
          propertyName: 'ClaimId',
          values: [String(action.payload.claimId)],
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

    EdsDebugService.logResponse(EdsEntityCodes.Payment, {
      rawResponse: data,
      normalizedResponse,
    });

    yield put(getPaymentsSuccess({ dataList: normalizedResponse, claimId: action.payload.claimId }));
  } catch (error) {
    console.error(error);
  }
}

function* getPaymentDetailsSaga({ payload: { paymentId } }: PayloadAction<GetPaymentDetailsPayload>) {
  try {
    if (!paymentId) return;

    const { data } = yield call(
      HttpService.get,
      `v2/${EdsEntityCodes.Payment}/${paymentId}?includeEntities=Payee.Party.Person,Payee.Party.Business,PaymentSchedule.PaymentSplits.PaymentMethod.Address`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, {
      section: EdsDefinitionSections.SidePanel,
    });

    if (data) {
      EdsDebugService.logResponse(EdsEntityCodes.Payment, {
        rawResponse: data,
        normalizedResponse,
        includedEntitiesRecord: normalizedResponse.entities,
      });

      yield put(getPaymentDetailsSuccess(normalizedResponse));
    }
  } catch (error) {
    yield put(getPaymentDetailsError());
    console.error(error);
  }
}

function* deletePaymentSaga({
  payload: { claimId, paymentId },
}: PayloadAction<{ claimId: number | string; paymentId: number | string }>) {
  try {
    yield call(HttpService.delete, `/${EdsEntityCodes.Payment}/delete-payment/${paymentId}`);
    yield put(getPayments({ claimId: Number(claimId) }));
  } catch (error) {
    console.error(error);
  }
}

function* deletePaymentSplitSaga({ payload }: PayloadAction<{ splitId: number; claimId: number }>) {
  try {
    yield call(HttpService.delete, `/${EdsEntityCodes.Payment}/payment-split/${payload.splitId}`);
    yield put(getPayments({ claimId: Number(payload.claimId) }));
  } catch (error) {
    console.error(error);
  }
}

function* deletePaymentPayeeSaga({
  payload: { payeeId, claimId, entityTypeToRefresh },
}: PayloadAction<{ claimId: number | string; payeeId: number | string; entityTypeToRefresh: AdjustmentEntities }>) {
  try {
    yield call(HttpService.delete, `/${EdsEntityCodes.Payment}/delete-payee/${payeeId}`);

    if (entityTypeToRefresh === AdjustmentEntities.Payment) {
      yield put(getPayments({ claimId: Number(claimId) }));
    }

    if (entityTypeToRefresh === AdjustmentEntities.Benefit) {
      yield put(getBenefitSectionDetails(String(claimId)));
    }

    SnacksService.add({
      type: SnackType.Success,
      title: `Payee was successfully deleted!`,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);
  }
}

function* createPaymentTransactionSaga({
  payload: { claimId, paymentId },
}: PayloadAction<{ claimId: string | number; paymentId: string | number }>) {
  try {
    const { data } = yield call(HttpService.post, `/${EdsEntityCodes.Transaction}/from-payment`, { paymentId });
    SnacksService.add({
      type: SnackType.Success,
      title: `Transaction ${data.transactionNumber} was successfully created!`,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(getPaymentTransactionsDetails({ paymentId }));

    yield* getPaymentsSaga(getPayments({ claimId: Number(claimId) }));
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to create transaction.',
      content: 'The system can not create this transaction.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    console.error(error);
  }
}

function* duplicatePaymentTransactionSaga({
  payload: { claimId, transactionId, paymentId },
}: PayloadAction<{ claimId: string | number; transactionId: string | number; paymentId: string | number }>) {
  try {
    yield call(HttpService.post, `/${EdsEntityCodes.Transaction}/duplicate`, { transactionId });

    yield put(getPaymentTransactionsDetails({ paymentId }));

    yield* getPaymentsSaga(getPayments({ claimId: Number(claimId) }));
  } catch (error) {
    console.error(error);
  }
}

export function* paymentsWatcher() {
  yield takeLatest([getPayments.type, addPaymentSuccess.type], getPaymentsSaga);
  yield takeLatest(deletePayment.type, deletePaymentSaga);
  yield takeLatest(getPaymentDetails.type, getPaymentDetailsSaga);
  yield takeLatest(createPaymentTransaction.type, createPaymentTransactionSaga);
  yield takeEvery(getPaymentTransactionsDetails.type, getPaymentTransactionsDetailsSaga);
  yield takeLatest(duplicatePaymentTransaction.type, duplicatePaymentTransactionSaga);
  yield takeLatest(deletePaymentPayee.type, deletePaymentPayeeSaga);
  yield takeLatest(deletePaymentSplit.type, deletePaymentSplitSaga);
}
